import React, { useState, useEffect, useRef, useCallback } from "react";
import { Eye, EyeOff, Clock } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";
import ReAuthModal from "./ReAuthModal";

const SSN_REVEAL_TIMEOUT_MS = 30000; // 30 seconds

/**
 * SSNCell — Displays masked SSN with Reveal/Hide controls and auto-re-mask timer.
 * The decrypted SSN is held ONLY in local React state; never in storage, URL, or logs.
 * Requires password re-auth before each reveal (HIPAA extra security layer).
 */
const SSNCell = ({ referralId, ssnField, maskedSsn, hasSsn, token, onAuditEvent }) => {
  const [revealed, setRevealed] = useState(false);
  const [fullSsn, setFullSsn] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [showReAuth, setShowReAuth] = useState(false);
  const [reAuthError, setReAuthError] = useState("");
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const hideSsn = useCallback(() => {
    setRevealed(false);
    setFullSsn("");
    setSecondsLeft(0);
    clearTimers();
  }, [clearTimers]);

  // Auto-hide on unmount (navigation away, modal close)
  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  // Listen for session expiry / logout broadcast
  useEffect(() => {
    const handler = () => hideSsn();
    window.addEventListener("admin-logout", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("admin-logout", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, [hideSsn]);

  const handleRevealClick = () => {
    if (!hasSsn) return;
    setReAuthError("");
    setShowReAuth(true);
  };

  const handleReAuthConfirm = async (password) => {
    setLoading(true);
    setReAuthError("");
    try {
      const data = await safeJsonRequest("/api/admin/reveal-ssn", {
        method: "POST",
        body: { referral_id: referralId, ssn_field: ssnField, password },
        token,
      });
      setFullSsn(data.ssn);
      setRevealed(true);
      setShowReAuth(false);
      setSecondsLeft(SSN_REVEAL_TIMEOUT_MS / 1000);
      if (onAuditEvent) onAuditEvent();

      // Start auto-hide timer
      timerRef.current = setTimeout(() => hideSsn(), SSN_REVEAL_TIMEOUT_MS);

      // Countdown display
      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const detail = typeof err.detail === "string" ? err.detail : "Failed to reveal SSN";
      if (err.status === 403) {
        setReAuthError("Incorrect password. Please try again.");
      } else {
        setReAuthError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!hasSsn) {
    return <span className="text-slate-500 text-sm italic">No SSN</span>;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <span
          className={`font-mono text-sm ${revealed ? "text-amber-300 font-bold" : "text-slate-300"}`}
          data-testid={`ssn-display-${ssnField}-${referralId}`}
        >
          {revealed ? fullSsn : maskedSsn}
        </span>

        {revealed ? (
          <div className="flex items-center gap-1">
            <button
              onClick={hideSsn}
              className="p-1.5 rounded-lg bg-slate-600 hover:bg-slate-500 transition-colors"
              title="Hide SSN"
              data-testid={`hide-ssn-${ssnField}-${referralId}`}
            >
              <EyeOff className="w-3.5 h-3.5 text-slate-300" />
            </button>
            <span className="text-xs text-amber-400 flex items-center gap-0.5" data-testid={`ssn-timer-${ssnField}-${referralId}`}>
              <Clock className="w-3 h-3" />
              {secondsLeft}s
            </span>
          </div>
        ) : (
          <button
            onClick={handleRevealClick}
            disabled={loading}
            className="p-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50"
            title="Reveal SSN"
            data-testid={`reveal-ssn-${ssnField}-${referralId}`}
          >
            <Eye className="w-3.5 h-3.5 text-white" />
          </button>
        )}
      </div>

      <ReAuthModal
        open={showReAuth}
        onClose={() => { setShowReAuth(false); setReAuthError(""); }}
        onConfirm={handleReAuthConfirm}
        loading={loading}
        error={reAuthError}
      />
    </>
  );
};

export default SSNCell;
